const VisualComponent = require('./VisualComponent');
const CollapsibleFigures = require('./helpers/CollapsibleFigures');

class Collateral extends VisualComponent {
    _escModals = [];
    _closeBound = null;
    _texts = {};
    _open = false;

    constructor(data, articleView, config, templates) {
        super();

        const collateral = this.getElement(articleView.getArticle(), config.collateral.s.main);
        if (!collateral) {
            console.debug("Collateral HTML hasn't been found. Constructor stopped.");
            return;
        }

        this.main = collateral;
        this.menu = this.getElement(articleView.getArticle(), config.collateral.s.menu);

        this.articleView = articleView;
        this._texts = articleView._texts;
        this.data = data;
        this.config = config;
        this._escModals = articleView?.escapeModals || [];

        this.s = config.classNames;
        this.icons = config.icons;
        this.selectors = config.collateral.s;

        this.collateralPrefix = this.selectors.prefix;
        this.collateralTabPrefix = this.selectors.tabPrefix;
        this.collateralTabIds = this.selectors.paneIds;
        this.keepSession = config.collateral.retainSessionStatus ?? false;

        this.tmpl = {
            figure: templates.figureTmpl,
        };
        this.collateralTexts = document.getElementById('collateral_texts_template');

        this.buildTabContent = this.buildTabContent.bind(this);
        this.closeOnMiss = this.closeOnMiss.bind(this);
        this.testInCollateral = this.testInCollateral.bind(this);
    }

    initiate() {
        if (!this.main) return;
        try {
            this.prepareContent();
            this.setScrollIndicator(this.config.pill.scrollIndicator, this.menu.closest('.collateral-pill'));
            this.addEventListeners();
        } catch (error) {
            console.debug('Collateral - initiate:', error);
        }
        try {
            this.config.enhancements.addDataIds && this.addDataIds();
        } catch (error) {
            console.debug('Collateral - Add data-id to elements:', error);
        }
        if (window.MathJax) {
            window.MathJax.typesetPromise && window.MathJax.typesetPromise();
        }
    }

    afterInit() {
        if (!this.main) return;
        if (this.keepSession) {
            this.testInCollateral(sessionStorage.getItem(sessionStorage.getItem('doi') + '_collateralPanel'));
        }
        if (!this._open && window.location.hash) {
            this.testInCollateral(window.location.hash);
        }
    }

    // Helper functions
    getElement(from, selector) {
        return from.querySelector(selector);
    }

    containIntoView() {
        if (!this.config.pill.containIntoView || !this.menu) return;
        this.restrainIntoView(this.menu, 64);
        this.articleView.articleFixedHeader.trackWindowScroll(this.menu, this.articleView.getArticle());
    }

    // Setting up the proper HTML and event listeners functions
    addEventListeners() {
        this.main.dispatchEvent(new Event('updateCitationsCounter'));
        this.articleView.subscribeAnchorClick('a', this.testInCollateral);

        if (this.config.pill.containIntoView) {
            this.containIntoView();
            this.articleView.subscribe('scroll', this.containIntoView.bind(this));
            this.articleView.subscribe('resize', this.containIntoView.bind(this));
        }
    }

    createTabPanel(id, icon, title, content) {
        const heading2 = document.createElement('h2');
        if (id === 'core-collateral-references') {
            heading2.setAttribute('id', 'ref-title');
        }
        heading2.append(icon);
        heading2.append(title[0]);

        const heading3 = document.createElement('h3');
        heading3.append(title[1] || title[0]);

        const header = document.createElement('header');
        header.append(heading2);

        const panel = document.createElement('div');
        panel.setAttribute('id', id);
        panel.setAttribute('role', 'tabpanel');
        panel.append(header, heading3);

        content && heading3.insertAdjacentHTML('afterend', content);

        return panel;
    }

    prepareContent() {
        const pillItems = this.getPillItems();

        let {updatedPillItems, hasMoreTab} = this.updateMoreTabPosition(pillItems);

        this.initializePillItems(updatedPillItems);

        this.configurePanelAttributesAndButtons(hasMoreTab);
    }

    getPillItems() {
        const pillItems = [];
        const listItems = this.menu.closest('.core-nav-wrapper')?.querySelectorAll('[class^="collateral-"]');
        listItems.forEach(li => pillItems.push(...li.querySelectorAll('a')));
        return pillItems;
    }

    updateMoreTabPosition(pillItems) {
        let updatedPillItems = pillItems;
        let hasMoreTab = false;
        pillItems.forEach((item, index) => {
            if (item.hash === '#' + this.collateralTabIds.more) {
                hasMoreTab = true;
                updatedPillItems.splice(index, 1);
                updatedPillItems.push(item);
            }
        });
        return {updatedPillItems, hasMoreTab};
    }

    initializePillItems(pillItems) {
        pillItems.forEach(item => {
            if (!item.hash) {
                this.enableAccessibly(item);
                return;
            }
            // If no corresponding collateral pane is found, we need to create it
            let panel = this.getPanel(item) || this.createNewPanel(item);
            // Then populate and/or hydrate the pane contents
            this.populatePanelContent(panel);
            // if the pane is empty, disable the trigger
            this.verifyPanelAvailability(panel, item);
        });
    }

    getPanel(item) {
        let panel = this.main.querySelector(item.getAttribute('href'));

        try {
            return panel;
        } catch (error) {
            console.debug('Populating missing pane:', panel, error);
        }
    }

    createNewPanel(item) {
        const panelId = item.getAttribute('href');
        const icon = item.querySelector('i').cloneNode(true);
        const title = item.querySelector('.sr-only, .link-text').innerHTML;
        const id = panelId.replace('#', '');
        this.main.append(this.createTabPanel(id, icon, [title]));
        return this.main.querySelector(item.getAttribute('href'));
    }

    populatePanelContent(panel) {
        try {
            this.buildTabContent(panel);
        } catch (error) {
            console.debug('Building/Hydrating pane content:', panel, error);
        }
    }

    verifyPanelAvailability(panel, item) {
        if (panel?.notAvailable) {
            const hasTitle = item.getAttribute('title');
            const hasAriaLabel = item.getAttribute('aria-label');
            if (hasTitle) {
                this.disableAccessibly(item);
                item.parentElement?.setAttribute('title', 'No ' + hasTitle.toLowerCase() + ' available');
            } else if (hasAriaLabel) {
                item.setAttribute('aria-label', 'No ' + hasAriaLabel.toLowerCase() + ' available');
                item.parentElement?.setAttribute('data-toggle', 'tooltip');
                item.parentElement?.setAttribute('data-placement', 'left');
                item.parentElement?.setAttribute(
                    'data-original-title',
                    'No ' + hasAriaLabel.toLowerCase() + ' available'
                );
            }
        } else {
            this.enableAccessibly(item);
            item.parentElement?.removeAttribute('title');
            item.parentElement?.removeAttribute('aria-label');
        }
    }

    configurePanelAttributesAndButtons(hasMoreTab) {
        this.panes = this.main.childNodes;
        this.panes.forEach(pane => {
            this.setTabBehavior(pane, this.config.tabs.collateral);
            pane.setAttribute('tabindex', '-1');
            pane.setAttribute('aria-labelledby', 'pane-' + pane.id);
            pane.querySelector('h2').setAttribute('id', 'pane-' + pane.id);

            let closeButton = this.createCloseButton();
            pane.querySelector('header').appendChild(closeButton);
            const isMore = pane.id === this.collateralTabIds.more;
            const isPurchaseAccess = pane.id === this.collateralTabIds.purchaseAccess;
            if (!(isMore || isPurchaseAccess) && hasMoreTab) {
                let backButton = this.createBackButton();
                pane.querySelector('header').prepend(backButton);
            }
        });
    }

    createBackButton() {
        // Append a back button at the section's header
        const backButton = document.createElement('button');
        backButton.setAttribute('data-back', 'collateral');
        backButton.setAttribute('aria-label', 'Back');
        backButton.innerHTML = `<i class="${this.icons.ICON_BACK}" />`;
        backButton.addEventListener('click', () => this.testInCollateral('#core-collateral-more'));
        return backButton;
    }

    createCloseButton() {
        // Append a close button at the section's header
        const closeButton = document.createElement('button');
        closeButton.setAttribute('data-close', 'collateral');
        closeButton.setAttribute('aria-label', 'Close');
        closeButton.innerHTML = `<i class="${this.icons.ICON_CLOSE}" />`;
        closeButton.addEventListener('click', this.closeCollateral.bind(this));
        return closeButton;
    }

    buildTabContent(tab) {
        const switchId = tab.id.replace(this.collateralPrefix, '');
        if (!this.config.isInEditor) tab.notAvailable = true;

        switch (switchId) {
            case 'info':
                this.buildInfoTab();
                break;
            case 'metrics':
                this.buildMetricsTab();
                break;
            case 'self-citation':
                const parentTab = this.main.querySelector('#' + this.selectors.paneIds.selfCitation);
                parentTab.notAvailable = false;
                break;
            case 'fulltext-options':
            case 'purchase-access':
                this.buildOptionsTab(switchId);
                this.config.collateral.hideDisabled = true;
                break;
            case 'references':
                this.buildReferencesTab();
                break;
            case 'figures':
            case 'media':
            case 'tables':
                this.buildFigureTab(switchId);
                break;
            case 'share':
                this.buildShareTab();
                break;
            case 'more':
                this.buildMoreTab();
                break;
            default:
                tab.notAvailable = false;
                console.debug('No such tab found...', switchId);
                break;
        }
    }

    addDataIds() {
        this.dataIds = this.config.enhancements.dataIds;
        for (const [key, value] of Object.entries(this.collateralTabIds)) {
            document.body
                .querySelectorAll(`[aria-controls="${value}"]`)
                .forEach(link => link.setAttribute('data-id', this.dataIds[key]));
            document.body
                .querySelectorAll(`[href="#${value}"]`)
                .forEach(link => link.setAttribute('data-id', this.dataIds[key]));
        }
    }

    removeEmptyDropZones(wrapperTab) {
        // if we are not in PBEditor remove all empty dropzone sections
        const sections = wrapperTab.childNodes;
        if (sections.length > 0 && !this.config.isInEditor) {
            [...sections].map(section => this.checkIfEmpty(section) && section.remove());
        }
    }

    testInCollateral(e) {
        let target;
        if (!e) return;
        if (typeof e === 'string') {
            if (e !== '#') {
                // clicking on an internal link
                const id = e.startsWith('#') ? e : '#' + e;
                target = this.main.querySelector(id);
            }
            e && e.preventDefault && e.preventDefault();
        } else {
            if (e.currentTarget.hash) {
                // clicking on an active menu element causes the pill to close
                if (e.currentTarget.getAttribute('aria-current') !== 'true') {
                    target = this.main.querySelector(e.currentTarget.hash);
                } else {
                    e && e.preventDefault && e.preventDefault();
                }
            } else if (
                (e.currentTarget.hash === '' && !!e.currentTarget.closest(this.selectors.main)) ||
                e.currentTarget.getAttribute('href') === 'javascript:void(0)'
            ) {
                // clicking on a link inside the collateral that doesn't go somewhere to the page
                // (e.g., it should have been a button, or it triggers a Literatum action)
                return;
            }
            // else no target === close collateral
        }

        document.removeEventListener('click', this.closeOnMiss);
        if (target) {
            let hash = typeof e === 'string' ? e : e.currentTarget.hash;
            hash = hash.substring(0, 1) === '#' ? hash.substring(1) : hash;

            let elem = this.main.querySelector("[id='" + hash + "']");
            let pane = elem ? elem.closest('[role="tabpanel"]') : null;

            if (!elem || !pane) {
                return;
            }
            e && e.preventDefault && e.preventDefault();
            this.#openCollateral(elem, pane, e);
        } else if (this._open) {
            this.closeCollateral(e.currentTarget.classList.contains('to-citation'));
        }
    }

    #openCollateral(elem, pane) {
        if (!this._closeBound) {
            this._closeBound = this.closeCollateral.bind(this);
        }
        this._escModals.push(this._closeBound);
        this.animatingCollateral(true);

        // if it's a subpanel - trigger it
        const tabButton = this.main.querySelector('[aria-controls="' + pane.id + '"]');
        if (tabButton) {
            tabButton.click();
            pane = tabButton.closest('[role="tabpanel"]');
        }

        // if the element click goes to a contributor expandable - trigger it
        const config = this.config.contributors;
        if (elem.getAttribute('property') === 'author' && config.expandOnFocus) {
            elem.querySelector(config.selectors.TRIGGER + '[aria-expanded="false"]')?.click();
        }
        this.activateMenuFor(pane); // this is not only menu activator but all triggers activator

        // things that should happen ONLY outside the PB...
        if (this.config.isInEditor) return;

        this.articleView.focusTrap?.init('collateral', pane || elem, {
            fallbackFocus: this.main.parentElement,
            initialFocus: elem,
        });

        const children = this.articleView.getArticle().children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child !== this.main) {
                child.setAttribute('aria-hidden', 'true');
            }
        }

        if (this.keepSession) {
            sessionStorage.setItem(sessionStorage.getItem('doi') + '_collateralPanel', elem.id || pane.id);
            let scrollingWrapper = pane.querySelector('.' + this.selectors.wrapper + ', [role="tabpanel"]');
            const sessionScrollTop = sessionStorage.getItem(
                sessionStorage.getItem('doi') + '_collateralPanelScrollPosition'
            );
            setTimeout(function () {
                if (Number(sessionScrollTop) === 0) {
                    elem.scrollIntoView({behavior: 'smooth', block: 'start'});
                } else {
                    scrollingWrapper.scrollTop = sessionScrollTop;
                }
            }, 500); // wait for the collateral animation to finish
            window.addEventListener('beforeunload', function () {
                const scrollPosition = scrollingWrapper?.scrollTop; // Get the vertical scroll position
                sessionStorage.setItem(
                    sessionStorage.getItem('doi') + '_collateralPanelScrollPosition',
                    scrollPosition
                );
            });
        }
        setTimeout(() => document.addEventListener('click', this.closeOnMiss));
    }

    closeCollateral(returnFocus = false) {
        if (this._escModals[this._escModals.length - 1] === this._closeBound) {
            this._escModals.pop();
            this._closeBound = null;
        }
        this.articleView.focusTrap?.deactivate({returnFocus});
        this.animatingCollateral(false);
        this.activateMenuFor(); // activating for nothing i.e., deactivating
        const children = this.articleView.getArticle().children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            child.removeAttribute('aria-hidden'); // or child.setAttribute('aria-hidden', 'false');
        }
        sessionStorage.removeItem(sessionStorage.getItem('doi') + '_collateralPanel');
        sessionStorage.removeItem(sessionStorage.getItem('doi') + '_collateralPanelScrollPosition');
        document.removeEventListener('click', this.closeOnMiss);
    }

    closeOnMiss(e) {
        if (
            !this.config.isInEditor &&
            !this.main.contains(e.target) &&
            e.target.isConnected &&
            !this.articleView.viewer?.main?.contains(e.target) &&
            e.target.getAttribute('href') !== 'javascript:void(0)'
        ) {
            this.closeCollateral();
        }
    }

    activateMenuFor(target = null) {
        /*
          calling this function without id will deactivate all the items
          if we get id we activate for id
          if we get id, but there's the lastId, and it's not like the new one, we deactivate for the old one and activate for the new one
          if there is no id -> deactivate old one
         */
        let triggers = [];

        if (this.lastTargetTriggers) {
            this.lastTargetTriggers.forEach(trigger => {
                trigger.removeAttribute('aria-current');
                this.main.querySelector(trigger.getAttribute('href')).setAttribute('tabindex', '-1');
            });
        }

        if (target) {
            triggers = Array.prototype.slice.call(document.body.querySelectorAll(`[href='#${target.id}']`));
            triggers.forEach(trigger => trigger.setAttribute('aria-current', 'true'));
            target.setAttribute('tabindex', '0');
        }

        this.lastTargetTriggers = triggers;
    }

    animatingCollateral(opening) {
        this._open = opening;
        if (opening) {
            this.main.classList.add('pill--open');
            document.body.classList.add('pill-lock-screen');
        } else {
            this.main.classList.remove('pill--open');
            document.body.classList.remove('pill-lock-screen');
        }
        document.body.classList.add('pill-lock-screen--transitioning');

        // LIT-498729 | Improve positioning of section navigation menu and pill
        if (this.menu && this.articleView.getArticle().getAttribute('data-design') === 'pill') {
            const {bottom} = this.menu.getBoundingClientRect();
            if (opening) {
                if (window.innerHeight < bottom) {
                    this.cssVar_set('--collateral-translate-y', window.innerHeight - bottom - 16 + 'px');
                }
            } else {
                this.cssVar_set('--collateral-translate-y', 0);
            }
        }

        clearTimeout(this.closingTimeout);
        this.closingTimeout = setTimeout(() => {
            document.body.classList.remove('pill-lock-screen--transitioning');
            !this._open && this.menu?.parentNode.removeAttribute('style');
        }, 400);
    }

    importCollapsableModule(figureType, elementsTab) {
        // TODO
        let CollapsibleFiguresList = [];

        if (figureType === 'figures' && this.config.collapsible.figures.enabled) {
            CollapsibleFiguresList = this.getCollapsibleFiguresList(elementsTab, this.config.collapsible.figures.wrap);
            if (CollapsibleFiguresList.length > 0) {
                this.collapsibleFigures = new CollapsibleFigures(
                    CollapsibleFiguresList,
                    this.config.collapsible.figures
                );
                this.collapsibleFigures.init();
            }
        }

        if (figureType === 'tables' && this.config.collapsible.tables.enabled) {
            CollapsibleFiguresList = this.getCollapsibleFiguresList(elementsTab, this.config.collapsible.tables.wrap);
            if (CollapsibleFiguresList.length > 0) {
                this.collapsibleTables = new CollapsibleFigures(CollapsibleFiguresList, this.config.collapsible.tables);
                this.collapsibleTables.init();
            }
        }
    }

    getCollapsibleFiguresList(elementsTab, wrapSelector) {
        const CollapsibleFiguresList = [];
        const wrappedFigures = elementsTab.querySelectorAll(wrapSelector);

        for (const figure of wrappedFigures) {
            CollapsibleFiguresList.push({
                ref: figure.closest('figure'),
            });
        }

        return CollapsibleFiguresList;
    }

    buildInfoTab() {
        const parentTab = this.main.querySelector('#' + this.collateralTabIds.info);
        const informationTab = this.main.querySelector('#' + this.collateralTabIds.information);
        if (!this.checkIfEmpty(informationTab, 'H3')) {
            parentTab.notAvailable = false;
            // if there is a request-permissions button, manipulate the HTML
            this.requestPermissionsLink(informationTab);
            // if there is a peer-review-history link, manipulate the HTML
            this.peerReviewHistoryLink(informationTab);
        }
        const contributorsTab = this.main.querySelector('#' + this.collateralTabIds.contributors);
        if (!this.checkIfEmpty(contributorsTab, 'H3')) {
            parentTab.notAvailable = false;
            // check if there are any customizations to the affiliation label
            this.customizeAffiliationsLabel(contributorsTab);
            // change the collateral authors HTML structure to be an accordion
            this.makeAuthorsList(contributorsTab);
            this.makeDisclosuresList(contributorsTab);
        }
    }

    buildMetricsTab() {
        if (this.config.isInEditor) return;
        const parentTab = this.main.querySelector('#' + this.collateralTabIds.metrics);
        let isEmpty = this.checkIfEmpty(parentTab);
        const metricsTab = this.main.querySelector('#' + this.collateralTabIds.metricsInner);
        if (metricsTab) {
            this.removeEmptyDropZones(metricsTab);
            isEmpty = this.checkIfEmpty(metricsTab, 'H3');
        }
        const citationTab = this.main.querySelector('#' + this.collateralTabIds.citations);
        if (citationTab) {
            this.removeEmptyDropZones(citationTab);
            isEmpty = this.checkIfEmpty(citationTab, 'H3');
            !isEmpty &&
                this.main.addEventListener('updateCitationsCounter', () => {
                    const citationsCount = document.querySelector('.citations-count');
                    if (citationTab && citationsCount === null) {
                        this.citations = citationTab.querySelectorAll(this.config.collateral.citedBySelector);
                        this.minCitations = 1;
                        if (this.citations.length) {
                            this.manageCitationCount(this.citations, this.minCitations);
                        }
                    }
                });
        }

        if (!isEmpty) {
            parentTab.notAvailable = false;
        }
    }

    buildFigureTab(switchId) {
        const wrapper = this.main.querySelector('#' + this.collateralTabIds[switchId]);
        if (this.config.isInEditor) {
            wrapper.notAvailable = false;
            return;
        }

        const linkConfig = (config, key) => {
            return {
                ...config[key],
                icon: this.icons[config[key].icon],
                ariaLabel: this._texts[config[key].ariaLabel],
                text: this._texts[config[key].text],
            };
        };

        const populatePane = (key, wrapper) => {
            const data = this.data.figures[key];
            if (data && data.length) {
                let config = this.deepMerge(
                    this.deepMerge(this.config.figures.all, this.config.figures[key]),
                    this.deepMerge(this.config.collateral.figures.all, this.config.collateral.figures[key])
                );
                config.linkOpenAllinViewer = {
                    ...linkConfig(config, 'linkOpenAllinViewer'),
                    href: '#' + this.config.figureViewer.s.prefix + '-panel_' + key,
                    classList: config.linkOpenAllinViewer.classList,
                };
                config.linkToContent = {...linkConfig(config, 'linkToContent')};
                config.linkToViewer = {
                    ...linkConfig(config, 'linkToViewer'),
                    classList: config.linkToViewer.classList,
                };
                config.linkToViewerAlt = {
                    ...linkConfig(config, 'linkToViewerAlt'),
                    classList: config.linkToViewerAlt.classList,
                };
                let panelData = this.tmpl.figure({data, key, ...config});
                if (wrapper) {
                    wrapper.notAvailable = false;
                    typeof panelData === 'string' ? (wrapper.innerHTML += panelData) : wrapper.appendChild(panelData);
                }

                if (this.config.collapsible.collateralEnabled) {
                    if (key === 'graphic') {
                        this.importCollapsableModule('figures', wrapper);
                    } else if (key === 'table') {
                        this.importCollapsableModule('tables', wrapper);
                    }
                }
            } else {
                wrapper.notAvailable = true;
            }
        };
        const testKeys = (key, container) => {
            switch (key) {
                case 'figures':
                    populatePane('graphic', container);
                    break;
                case 'other':
                case 'media':
                    populatePane('media', container);
                    break;
                case 'tables':
                    populatePane('table', container);
                    break;
            }
        };

        testKeys(switchId, wrapper);
        if (wrapper.notAvailable) {
            // check if it has tabs inside
            wrapper.querySelectorAll(this.config.tabs.collateral.paneSelector).forEach(tab => {
                testKeys(tab.id.split(this.collateralTabPrefix)[1], tab);
                if (!tab.notAvailable) wrapper.notAvailable = false;
            });
            // check if it has populated dropzones inside
            if (wrapper.notAvailable && !!wrapper.querySelector('.' + this.selectors.wrapper)) {
                this.removeEmptyDropZones(wrapper.querySelector('.' + this.selectors.wrapper));
                wrapper.notAvailable = this.checkIfEmpty(wrapper.querySelector('.' + this.selectors.wrapper), 'H3');
            }
        }
    }

    buildOptionsTab(switchId) {
        const fulltextOptionsTab = this.main.querySelector('#' + this.collateralTabIds.fulltextOptions);
        let parentTab = fulltextOptionsTab;
        if (this.config.isInEditor) {
            const header = parentTab.querySelector('header h2');
            header.childNodes[1].nodeValue = 'Get Access / View Options';
            parentTab.querySelectorAll('.' + this.selectors.wrapper).forEach(i => (i.tabIndex = 0));
        }

        const currentSection = fulltextOptionsTab.querySelector('[tabindex="0"]');
        const otherSection = fulltextOptionsTab.querySelector('[tabindex="-1"]');
        if (switchId === 'purchase-access') {
            parentTab = this.main.querySelector('#' + this.collateralTabIds.purchaseAccess);
            parentTab.appendChild(currentSection);
            fulltextOptionsTab.remove();
        }
        currentSection.removeAttribute('tabindex');
        otherSection?.remove();

        if (this.checkIfEmpty(parentTab.querySelector('.' + this.selectors.wrapper), 'H3')) return;
        parentTab.notAvailable = false;
    }

    buildReferencesTab() {
        if (this.config.isInEditor) return;
        const parentTab = this.main.querySelector('#' + this.selectors.paneIds.references);
        parentTab.setAttribute('aria-labelledby', 'ref-title');
        parentTab.notAvailable = true;
        try {
            const references = this.articleView.getArticle().querySelector(this.selectors.citations);
            if (!references) return;
            const list = references.cloneNode(true);
            list.classList.add(this.selectors.wrapper);
            parentTab.appendChild(list);
            list.querySelectorAll('[id]').forEach(element => {
                const newId = this.collateralPrefix + element.id;
                list.querySelectorAll('[aria-controls="' + element.id + '"]').forEach(link => {
                    link.setAttribute('aria-controls', newId);
                });
                element.id = newId;
            });
            parentTab.notAvailable = false;
        } catch (error) {
            console.debug('Clone citations:', error);
        }
        try {
            this.addReferencesCount(parentTab);
        } catch (e) {
            console.error(e.message);
        }
    }

    addReferencesCount(referencePanel, config = this.config.pill.referencesCount) {
        if (!config.enabled) return;
        const button = this.articleView.getArticle().querySelector('[data-id=article-nav-menubar-references]');
        const collateralCitations = referencePanel.querySelectorAll('.' + this.selectors.wrapper + ' > div');
        if (collateralCitations) {
            const count = collateralCitations.length;
            if (button) {
                const indicator = document.createElement('span');
                indicator.classList.add('reference-count');
                indicator.innerHTML = count > config.maxCount ? config.maxCount + '+' : count;
                button.appendChild(indicator);
            }
        }
    }

    buildShareTab() {
        if (this.config.isInEditor) return;
        const parentTab = this.main.querySelector('#' + this.collateralTabIds.share);
        if (this.checkIfEmpty(parentTab)) return;
        parentTab.notAvailable = false;
        this.manageShareLink();
    }

    buildMoreTab() {
        const parentTab = this.main.querySelector('#' + this.collateralTabIds.more);
        const content = this.menu.cloneNode(true);
        content.setAttribute('role', 'menu');
        content.removeAttribute('data-core-nav');
        content.querySelectorAll('.sr-only').forEach(text => text.classList.remove('sr-only'));
        const wrapper = document.createElement('div');
        wrapper.classList.add(this.selectors.wrapper);
        wrapper.innerHTML = content.outerHTML;
        parentTab.appendChild(wrapper);
        if (this.checkIfEmpty(parentTab)) return;
        parentTab.notAvailable = false;
    }

    requestPermissionsLink(parent) {
        const config = this.config.collateral.permissions;
        const section = parent.querySelector(config.identifier);
        if (section) {
            const link = section.querySelector(config.linkSelector);
            const removeCondition = eval(config.removeCondition);
            if (removeCondition) {
                section.remove();
            } else if (link) {
                link.innerHTML = '';
                link.append(this.collateralTexts.content.cloneNode(true));
            }
        }
    }

    peerReviewHistoryLink(tab) {
        const config = this.config.collateral.peerReviewHistory;
        const link = tab.querySelector(config.selector);
        if (link) {
            link.innerHTML = config.innerHTML;
            link.classList = eval(config.classList).join(' ');
        }
    }

    customizeAffiliationsLabel(tab) {
        const header = tab.querySelector('.core-authors > h4');
        if (this.config.collateral.affiliationsTitle !== undefined) {
            // it has to be removed
            if (this.config.collateral.affiliationsTitle === '') {
                // it has to be an empty span
                header.outerHTML = '<div class="expand-all-wrapper"></div>';
            } else {
                // it has a different text
                header.innerHTML = this.config.collateral.affiliationsTitle;
            }
        }
    }

    makeAuthorsList(tab) {
        this.setExpandableBehavior({
            classes: {
                CHEVRON: this.icons.ICON_ACCORDION_ARROW,
                COLLAPSED_ICON: this.icons.ICON_ACCORDION_COLLAPSED,
                EXPANDED_ICON: this.icons.ICON_ACCORDION_EXPANDED,
            },
            selectors: {
                ...this.config.contributors.selectors,
                LIST: tab.querySelectorAll(this.config.contributors.selectors.LIST),
                TOGGLE_ALL_WRAPPER: tab.querySelector(this.config.contributors.selectors.TOGGLE_ALL_WRAPPER),
            },
            settings: {
                HEADING_LEVEL: this.config.collateral.affiliationsTitle === '' ? 'H4' : 'H5',
                ...this.config.contributors.settings,
            },
        });
    }

    makeDisclosuresList(tab) {
        this.setExpandableBehavior({
            classes: {
                CHEVRON: this.icons.ICON_ACCORDION_ARROW,
                COLLAPSED_ICON: this.icons.ICON_ACCORDION_COLLAPSED,
                EXPANDED_ICON: this.icons.ICON_ACCORDION_EXPANDED,
            },
            selectors: {
                CONTENT: '.disclosure-content',
                LIST: tab.querySelectorAll(
                    '[property="author"] .disclosure, [property="editor"] .disclosure, [property="reviewer"] .disclosure'
                ),
                TRIGGER: '.disclosure-heading',
                TOGGLE_ALL: undefined,
                TOGGLE_ALL_WRAPPER: undefined,
                TOGGLE_TEMPLATE: '',
            },
            settings: {
                HEADING_LEVEL: 'H6',
                TOGGLE_ALL_POSITION: '',
                WITH_CHEVRON: true,
                WITH_TOGGLE_ALL: false,
            },
        });
    }

    manageCitationCount(citations, minCitations) {
        const button = this.main.querySelector('[aria-controls="' + this.collateralTabIds.citations + '"]');
        const total = citations[0]?.parentNode.dataset?.total || citations.length;
        if (total >= minCitations && button) {
            const indicator = document.createElement('span');
            indicator.classList.add('citations-count');
            indicator.innerHTML = total;
            button.appendChild(indicator);
        }
    }

    manageShareLink() {
        const config = this.config.collateral.share;
        const share = this.main.querySelector(config.selectors.wrapper);
        if (share) {
            const shareLink = share.querySelector(config.selectors.source);
            const shareAction = share.querySelector(config.selectors.action);

            const newURL = window.location.origin + window.location.pathname;
            shareLink.href = shareLink.innerHTML = newURL;

            const copyShareLink = (value, config) => {
                const share = this.main.querySelector(config.selectors.wrapper);
                const shareStatus = share.querySelector(config.selectors.status);
                navigator.clipboard.writeText(value).then(
                    () => {
                        shareStatus.classList.add('success');
                        setTimeout(() => {
                            shareStatus.classList.remove('success');
                        }, config.delay);
                    },
                    () => {
                        shareStatus.classList.add('failed');
                        setTimeout(() => {
                            shareStatus.classList.remove('failed');
                        }, config.delay);
                    }
                );
            };

            shareAction.addEventListener('click', e => {
                e.preventDefault();
                copyShareLink(newURL, config);
            });
        }
    }
}

module.exports = Collateral;
